import { useContext, useEffect, useRef } from 'react';

import { RealtimeContext } from './realtimeContext';
import type { PublicationHandler, RealtimeStatus } from './types';

/** Connection state. `disabled` when `VITE_CENTRIFUGO_URL` is unset. */
export function useRealtimeStatus(): RealtimeStatus {
  return useContext(RealtimeContext).status;
}

/** True while the socket is live — use it to relax polling, never to skip it. */
export function useIsRealtimeConnected(): boolean {
  return useRealtimeStatus() === 'connected';
}

/**
 * Watch one channel for as long as the component is mounted.
 *
 * `channel` may be null/undefined while an id is still loading — nothing is
 * registered until it resolves. The handler is read through a ref so a new
 * closure on every render does not churn the subscription.
 */
export function useRealtimeChannel<T>(
  channel: string | null | undefined,
  handler: PublicationHandler<T>
): void {
  const { subscribe } = useContext(RealtimeContext);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!channel) return;
    return subscribe<T>(channel, data => handlerRef.current(data));
  }, [channel, subscribe]);
}
